import React, { useMemo, useState } from 'react';
import { OnboardingV2State } from '../../../../store/onboardingV2/types.ts';
import ProfilePhotoUploadV2 from '../../ProfilePhotoUploadV2/ProfilePhotoUploadV2.tsx';
import logoEdify from '../../../../assets/images/Logo-edify-no-bg.png';
import styles from './SummaryScreen.module.scss';

interface SummaryScreenProps {
  state: OnboardingV2State;
  onAction: (action: string, data?: any) => void;
}

const SummaryScreen: React.FC<SummaryScreenProps> = ({ state, onAction }) => {
  const [expandedSections, setExpandedSections] = useState({
    skills: false,
    profiles: false,
    zones: false,
  });
  const maxItems = 5;
  const isPerson = state.accountType === 'PERSON';
  const personaForm = state.personaForm;
  const empresaForm = state.empresaForm;
  const displayName = isPerson
    ? `${personaForm.name || ''} ${personaForm.lastName || ''}`.trim()
    : empresaForm.companyName || '';
  const telValue = isPerson ? personaForm.tel : empresaForm.tel;
  const webSiteValue = isPerson ? personaForm.webSite : empresaForm.webSite;
  const bioValue = isPerson ? personaForm.bio : empresaForm.bio;
  const descriptionValue = typeof bioValue === 'string' ? bioValue.trim() : '';
  const skillNames = state.userSkills.map((skill) => skill.name);
  const skillCategories = state.skillCategories || [];
  const profileCategoryNames = useMemo(() => {
    return state.preferredProfileCategories
      .map((categoryId) => skillCategories.find((category) => category.id === categoryId)?.name)
      .filter(Boolean) as string[];
  }, [state.preferredProfileCategories, skillCategories]);
  const zoneNames = state.allCountry
    ? ['Todo el país']
    : state.workZones.map((zone: any) => {
        const jurisdictionName = zone.jurisdiccion?.name || zone.name || '';
        const cityName = zone.city || '';
        return cityName ? `${jurisdictionName} - ${cityName}` : jurisdictionName;
      });
  const toggleSection = (section: 'skills' | 'profiles' | 'zones') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className={styles.interactiveContainer}>
      <div className={styles.summarySection}>
        <div className={styles.summaryHeader}>
          <div className={styles.summaryHeaderRow}>
            <div className={styles.summaryTitle}>Resumen de tu perfil</div>
            <img src={logoEdify} alt="Edify" className={styles.summaryLogo} />
          </div>
          <div className={styles.summarySubtitle}>
            Revisá los datos antes de confirmar.
          </div>
        </div>
        <div className={styles.summaryMain}>
          <div className={styles.summaryPrimary}>
            <div className={styles.summaryIdentity}>
              <ProfilePhotoUploadV2 />
              <div className={styles.summaryIdentityInfo}>
                <div className={styles.summaryPrimaryLabel}>{isPerson ? 'Profesional' : 'Empresa'}</div>
                <div className={styles.summaryPrimaryValue}>{displayName}</div>
              </div>
            </div>
            {descriptionValue && (
              <div className={styles.summaryPrimaryDescription}>{descriptionValue}</div>
            )}
          </div>
          <div className={styles.summaryDetails}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Tipo de cuenta</span>
              <span className={styles.summaryValue}>{isPerson ? 'Profesional' : 'Empresa'}</span>
            </div>
            {telValue && (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Teléfono</span>
                <span className={styles.summaryValue}>{telValue}</span>
              </div>
            )}
            {webSiteValue && (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Sitio web</span>
                <span className={styles.summaryValue}>{webSiteValue}</span>
              </div>
            )}
            {descriptionValue && (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>Descripción</span>
                <span className={styles.summaryValue}>{descriptionValue}</span>
              </div>
            )}
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Habilidades</span>
              <div className={styles.summaryValueList}>
                {skillNames.length > 0 ? (
                  (expandedSections.skills ? skillNames : skillNames.slice(0, maxItems)).map((skill) => (
                    <span
                      key={skill}
                      className={styles.summaryValueItem}
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <span
                    key="skills-empty"
                    className={styles.summaryValueItem}
                  >
                    Sin habilidades
                  </span>
                )}
                {skillNames.length > maxItems && (
                  <button
                    className={styles.summaryAccordionToggle}
                    onClick={() => toggleSection('skills')}
                    type="button"
                  >
                    {expandedSections.skills ? 'Ver menos' : `+${skillNames.length - maxItems}`}
                  </button>
                )}
              </div>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Con quién conectar</span>
              <div className={styles.summaryValueList}>
                {profileCategoryNames.length > 0 ? (
                  (expandedSections.profiles ? profileCategoryNames : profileCategoryNames.slice(0, maxItems)).map((category) => (
                    <span
                      key={category}
                      className={styles.summaryValueItem}
                    >
                      {category}
                    </span>
                  ))
                ) : (
                  <span
                    key="profiles-empty"
                    className={styles.summaryValueItem}
                  >
                    Sin categorías
                  </span>
                )}
                {profileCategoryNames.length > maxItems && (
                  <button
                    className={styles.summaryAccordionToggle}
                    onClick={() => toggleSection('profiles')}
                    type="button"
                  >
                    {expandedSections.profiles ? 'Ver menos' : `+${profileCategoryNames.length - maxItems}`}
                  </button>
                )}
              </div>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Zonas</span>
              <div className={styles.summaryValueList}>
                {zoneNames.length > 0 ? (
                  (expandedSections.zones ? zoneNames : zoneNames.slice(0, maxItems)).map((zone) => (
                    <span
                      key={zone}
                      className={styles.summaryValueItem}
                    >
                      {zone}
                    </span>
                  ))
                ) : (
                  <span
                    key="zones-empty"
                    className={styles.summaryValueItem}
                  >
                    Sin zonas
                  </span>
                )}
                {zoneNames.length > maxItems && (
                  <button
                    className={styles.summaryAccordionToggle}
                    onClick={() => toggleSection('zones')}
                    type="button"
                  >
                    {expandedSections.zones ? 'Ver menos' : `+${zoneNames.length - maxItems}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.summaryActions}>
          <button
            className={styles.completeButton}
            onClick={() => onAction('confirmSummary')}
            type="button"
          >
            Confirmar y finalizar
          </button>
          <button
            className={styles.summaryEditButton}
            onClick={() => onAction('summaryEdit')}
            type="button"
          >
            Editar pasos
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryScreen;
